import { uniqueRelayUrls } from "@/utils/url"
import { KINDS, tagValue, type DraftEvent, type NostrEvent, type NostrFilter } from "../types"

export function parseReactionEvent(event: NostrEvent): {
  targetEventId?: string
  targetPubkey?: string
  emoji: string
} {
  const emoji = event.content.trim() || "+"
  return {
    targetEventId: tagValue(event, "e"),
    targetPubkey: tagValue(event, "p"),
    emoji,
  }
}

/** A kind 6 repost may inline the reposted event in its content. */
export function parseRepostEvent(event: NostrEvent): {
  targetEventId?: string
  targetPubkey?: string
  embedded?: NostrEvent
} {
  let embedded: NostrEvent | undefined
  if (event.content.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(event.content) as NostrEvent
      if (parsed && typeof parsed.id === "string" && typeof parsed.kind === "number") embedded = parsed
    } catch {
      // plain reposts have empty content
    }
  }
  return {
    targetEventId: tagValue(event, "e") ?? embedded?.id,
    targetPubkey: tagValue(event, "p") ?? embedded?.pubkey,
    embedded,
  }
}

export function buildReactionDraft(target: NostrEvent, emoji = "+"): DraftEvent {
  return {
    kind: KINDS.REACTION,
    content: emoji,
    tags: [
      ["e", target.id, "", target.pubkey],
      ["p", target.pubkey],
      ["k", String(target.kind)],
    ],
    created_at: Math.floor(Date.now() / 1000),
  }
}

export function buildRepostDraft(target: NostrEvent, relayHint = ""): DraftEvent {
  return {
    kind: KINDS.REPOST,
    content: JSON.stringify(target),
    tags: [
      ["e", target.id, relayHint, target.pubkey],
      ["p", target.pubkey],
      ["k", String(target.kind)],
    ],
    created_at: Math.floor(Date.now() / 1000),
  }
}

/** NIP-09 deletion request for the given event ids. */
export function buildDeleteDraft(eventIds: string[], reason = ""): DraftEvent {
  return {
    kind: KINDS.DELETE,
    content: reason,
    tags: eventIds.map((id) => ["e", id]),
    created_at: Math.floor(Date.now() / 1000),
  }
}

export function parseContactList(event: NostrEvent | undefined): string[] {
  if (!event || event.kind !== KINDS.CONTACT_LIST) return []
  const pubkeys: string[] = []
  for (const tag of event.tags) {
    if (tag[0] !== "p" || !/^[0-9a-f]{64}$/.test(tag[1] ?? "")) continue
    if (!pubkeys.includes(tag[1])) pubkeys.push(tag[1])
  }
  return pubkeys
}

/** Rebuilds a kind 3 contact list, keeping relay hints and unknown tags. */
export function buildContactListDraft(
  pubkeys: string[],
  previousTags: string[][] = [],
): DraftEvent {
  const previous = new Map<string, string[]>()
  for (const tag of previousTags) {
    if (tag[0] === "p" && tag[1]) previous.set(tag[1], tag)
  }
  const preserved = previousTags.filter((tag) => tag[0] !== "p" && tag[0] !== "content")
  const tags = pubkeys.map((pubkey) => previous.get(pubkey) ?? ["p", pubkey])
  return {
    kind: KINDS.CONTACT_LIST,
    content: "",
    tags: [...tags, ...preserved],
    created_at: Math.floor(Date.now() / 1000),
  }
}

export interface RelayListEntry {
  url: string
  read: boolean
  write: boolean
}

const RELAY_TAG_SCOPES: Record<string, { read: boolean; write: boolean }> = {
  read: { read: true, write: false },
  write: { read: false, write: true },
  both: { read: true, write: true },
}

/** Parses a NIP-65 relay list (kind 10002). */
export function parseRelayListEvent(event: NostrEvent | undefined): RelayListEntry[] {
  if (!event || event.kind !== KINDS.RELAY_LIST) return []
  const entries: RelayListEntry[] = []
  for (const [name, url, scope = "both"] of event.tags) {
    if (name !== "r" || !url) continue
    const permissions = RELAY_TAG_SCOPES[scope] ?? RELAY_TAG_SCOPES.both
    entries.push({ url, ...permissions })
  }
  return entries
}

export function buildRelayListDraft(entries: RelayListEntry[], previousContent = ""): DraftEvent {
  const seen = new Set<string>()
  const tags: string[][] = []
  for (const entry of entries) {
    const url = uniqueRelayUrls([entry.url])[0]
    if (!url || seen.has(url)) continue
    seen.add(url)
    if (entry.read && entry.write) tags.push(["r", url])
    else if (entry.read) tags.push(["r", url, "read"])
    else if (entry.write) tags.push(["r", url, "write"])
  }
  return {
    kind: KINDS.RELAY_LIST,
    content: previousContent,
    tags,
    created_at: Math.floor(Date.now() / 1000),
  }
}

/** Parses a kind 10000 mute list (NIP-51). Read-only: the client displays it. */
export function parseMuteListEvent(event: NostrEvent | undefined): {
  pubkeys: string[]
  hashtags: string[]
  words: string[]
  threads: string[]
  event?: NostrEvent
} {
  if (!event || event.kind !== KINDS.MUTE_LIST) {
    return { pubkeys: [], hashtags: [], words: [], threads: [] }
  }
  const pubkeys: string[] = []
  const hashtags: string[] = []
  const words: string[] = []
  const threads: string[] = []
  for (const tag of event.tags) {
    const [name, value] = tag
    if (!value) continue
    if (name === "p" && /^[0-9a-f]{64}$/.test(value) && !pubkeys.includes(value)) pubkeys.push(value)
    else if (name === "t" && !hashtags.includes(value)) hashtags.push(value)
    else if (name === "word" && !words.includes(value)) words.push(value)
    else if (name === "e" && !threads.includes(value)) threads.push(value)
  }
  return { pubkeys, hashtags, words, threads, event }
}

/**
 * Updates a kind 0 metadata document, preserving fields this client does not
 * edit (e.g. `display_name`, custom keys other clients wrote).
 */
export function buildProfileDraft(
  fields: Record<string, string | undefined>,
  previous: Record<string, unknown> = {},
  pubkey?: string,
): DraftEvent {
  const metadata: Record<string, unknown> = { ...previous }
  for (const [key, value] of Object.entries(fields)) {
    const trimmed = typeof value === "string" ? value.trim() : ""
    if (trimmed) metadata[key] = trimmed
    else delete metadata[key]
  }
  if (pubkey && !metadata.display_name && metadata.name) metadata.display_name = metadata.name
  return {
    kind: KINDS.PROFILE,
    content: JSON.stringify(metadata),
    tags: [],
    created_at: Math.floor(Date.now() / 1000),
  }
}

export function parseBookmarkListEvent(event: NostrEvent | undefined): {
  noteIds: string[]
  addresses: string[]
} {
  if (!event || event.kind !== KINDS.BOOKMARK_LIST) return { noteIds: [], addresses: [] }
  const noteIds: string[] = []
  const addresses: string[] = []
  for (const tag of event.tags) {
    if (tag[0] === "e" && /^[0-9a-f]{64}$/.test(tag[1] ?? "") && !noteIds.includes(tag[1])) {
      noteIds.push(tag[1])
    }
    if (tag[0] === "a" && typeof tag[1] === "string" && !addresses.includes(tag[1])) {
      addresses.push(tag[1])
    }
  }
  return { noteIds, addresses }
}

export function buildBookmarkListDraft(
  state: { noteIds: string[]; addresses: string[] },
  previousContent = "",
): DraftEvent {
  return {
    kind: KINDS.BOOKMARK_LIST,
    content: previousContent,
    tags: [...state.noteIds.map((id) => ["e", id]), ...state.addresses.map((a) => ["a", a])],
    created_at: Math.floor(Date.now() / 1000),
  }
}

export function isMentionedIn(event: NostrEvent, pubkey: string): boolean {
  return event.tags.some((tag) => tag[0] === "p" && tag[1] === pubkey)
}

/** Reaction / repost / reply counters scoped to one note id. */
export function engagementFilters(noteId: string): NostrFilter[] {
  return [
    { kinds: [KINDS.REACTION], "#e": [noteId], limit: 500 },
    { kinds: [KINDS.REPOST], "#e": [noteId], limit: 200 },
    { kinds: [KINDS.NOTE], "#e": [noteId], limit: 200 },
  ]
}
